<template>
  <div class="map-selector-entry">
    <!-- 地图选择按钮 -->
    <Button class="map-selector-trigger" @click="openMapSelector" type="primary" icon="ios-map" size="small">
      选择地图
    </Button>

    <!-- 地图选择模态框 -->
    <Modal
      v-model="mapSelectorVisible"
      width="900"
      :styles="{ top: '50px' }"
      footer-hide
      class-name="map-selector-modal"
    >
      <div class="map-selector-container">


        <!-- 等级筛选标签（仅在大地图层级显示，或应用到当前区域） -->
        <div v-if="!currentBigMap" class="level-filter">
          <span class="filter-label">地图筛选：</span>
          <RadioGroup v-model="selectedLevelRange" type="button" @on-change="filterByLevel">
            <Radio label="all">全部</Radio>
            <Radio label="single">单人</Radio>
            <Radio label="dungeon">副本</Radio>
            <Radio label="activity">活动</Radio>
          </RadioGroup>
          <Button
            class="map-filter-battle-button"
            type="primary"
            icon="ios-play"
            size="small"
            :disabled="!selectedMap"
            :loading="battleLoading"
            @click="startBattle"
          >
            {{ battleLoading ? '进入战斗中...' : '开始战斗' }}
          </Button>
        </div>

        <div v-if="!currentBigMap && (selectedLevelRange === 'dungeon' || selectedLevelRange === 'activity')" class="sub-map-container special-map-container">
          <div class="current-area-info">
            <Alert show-icon :type="selectedLevelRange === 'activity' ? 'info' : 'warning'">
              <template slot="desc">
                {{ selectedLevelRange === 'activity' ? '限时活动地图一览' : '组队副本地图一览' }}
              </template>
            </Alert>
          </div>

          <div class="dungeon-grid-wrap">
            <div v-if="dungeonLoading" class="dungeon-loading">
              <Spin size="large" fix></Spin>
            </div>

            <div class="sub-map-grid">
            <div
              v-for="subMap in specialFilteredMaps"
              :key="subMap.mapId"
              class="sub-map-card"
              :class="{
                'selected': selectedMap && selectedMap.mapId === subMap.mapId,
                'recommended': isRecommended(subMap)
              }"
              @click="selectSubMap(subMap)"
            >
              <div class="sub-map-header">
                <Icon :type="getMapIcon(subMap)" class="map-icon" />
                <span class="sub-map-name">{{ subMap.mapName }}</span>
              </div>

              <div class="sub-map-details">
                <div class="detail-row">
                  <span class="label">推荐等级</span>
                  <span class="value highlight">Lv.{{ subMap.minLv }} - Lv.{{ subMap.maxLv }}</span>
                </div>
                <div class="detail-row">
                  <span class="label">在线修士</span>
                  <span class="value">
                    <Icon type="ios-people" size="12" /> {{ subMap.onlineNum || 0 }}人
                  </span>
                </div>
                <div class="detail-row">
                  <span class="label">掉落情报</span>
                  <span class="value drops">
                    <Poptip trigger="click" word-wrap width="360" placement="top" class-name="drop-preview-poptip" transfer>
                      <Button size="small" type="text" class="drop-detail-btn">
                        <Icon type="ios-book-outline" /> 查看掉落
                      </Button>
                      <div slot="content" class="drop-preview-content">
                        <template v-if="getMapDrops(subMap) && getMapDrops(subMap).itemsVoList && getMapDrops(subMap).itemsVoList.length > 0">
                          <div class="drop-preview-title">掉落物品</div>
                          <div class="drop-preview-items">
                            <span
                              v-for="(item, idx) in getSortedDropItems(getMapDrops(subMap).itemsVoList)"
                              v-if="item"
                              :key="idx"
                              class="drop-preview-item"
                              :style="{ color: getDropColor(item.color) }"
                            >{{ getDropDisplayName(item.itemName) }}({{ getDropQualityText(item.color) }}/Lv.{{ item.level || 0 }})</span>
                          </div>
                        </template>
                        <div v-else class="drop-preview-empty">暂无掉落数据</div>
                      </div>
                    </Poptip>
                    <Button size="small" type="text" class="drop-detail-btn" @click.stop="viewMapMonsters(subMap)">
                      <Icon type="ios-skull-outline" /> 查看怪物
                    </Button>
                  </span>
                </div>
                <div class="detail-row description-row">
                  <span class="label">描述</span>
                  <Poptip
                    trigger="hover"
                    word-wrap
                    width="220"
                    placement="top"
                    :content="subMap.describeTion"
                    :disabled="!subMap.describeTion || subMap.describeTion.length < 15"
                    class="description-poptip"
                  >
                    <span class="value description-text" :title="subMap.describeTion">
                      {{ subMap.describeTion || '暂无描述' }}
                    </span>
                  </Poptip>
                </div>
              </div>

              <div v-if="selectedMap && selectedMap.mapId === subMap.mapId" class="selected-mark">
                <Icon type="ios-checkmark-circle" color="#19be6b" size="24" />
              </div>

              <div v-if="isRecommended(subMap)" class="recommend-tag">
                <Icon type="ios-star" /> 推荐
              </div>
            </div>
          </div>
          </div>

          <div class="action-bar">
            <div class="selected-info" v-if="selectedMap">
              <span class="info-label">已选择：</span>
              <Tag color="primary" size="large">{{ selectedMap.mapName }}</Tag>
              <span class="level-hint">
                (推荐等级: Lv.{{ selectedMap.minLv }}-{{ selectedMap.maxLv }})
              </span>
            </div>
            <div class="action-buttons">
              <Button @click="refreshSubMaps" icon="ios-refresh" type="default">
                刷新
              </Button>
              <Button
                @click="startBattle"
                type="primary"
                icon="ios-play"
                :disabled="!selectedMap"
                :loading="battleLoading"
                size="large"
                class="battle-btn"
              >
                {{ battleLoading ? '进入战斗中...' : '开始战斗' }}
              </Button>
            </div>
          </div>
        </div>

        <!-- 普通地图按最低等级分为六个阶段 -->
        <div v-else-if="!currentBigMap" class="big-map-grid">
          <div
            v-for="stage in stageGroups"
            :key="stage.key"
            class="stage-map-section"
            :class="'stage-map-section--' + stage.theme"
          >
            <div class="stage-map-header">
              <div class="stage-map-title">
                <Icon :type="stage.icon" />
                <strong>{{ stage.label }}</strong>
                <span class="stage-map-range">{{ stage.range }}</span>
              </div>
              <small>{{ stage.hint }}</small>
            </div>
            <div v-if="stage.maps.length" class="stage-map-list">
              <div
                v-for="map in stage.maps"
                :key="map.mapId"
                class="stage-map-item"
                :class="{ selected: selectedMap && selectedMap.mapId === map.mapId }"
                @click="selectSubMap(map)"
              >
                <Icon :type="getMapIcon(map)" class="stage-map-item-icon" :style="{ color: getMapAccentColor(map) }" />
                <span class="stage-map-item-main">
                  <strong :style="{ color: getMapAccentColor(map) }">{{ map.mapName }}</strong>
                  <small>Lv.{{ map.minLv || 1 }} - {{ map.maxLv || map.minLv || 1 }} · {{ map.onlineNum || 0 }}人在线</small>
                  <span class="stage-map-item-description">
                    <Poptip
                      trigger="hover"
                      word-wrap
                      width="260"
                      placement="top"
                      :content="map.describeTion || '暂无描述'"
                      :disabled="!map.describeTion || map.describeTion.length < 18"
                    >
                      {{ map.describeTion || '暂无地图描述' }}
                    </Poptip>
                  </span>
                </span>
                <span class="stage-map-item-actions" @click.stop>
                  <Poptip trigger="click" word-wrap width="440" placement="top" class-name="drop-preview-poptip" transfer>
                    <Button size="small" type="text" title="点击查看掉落">
                      <Icon type="ios-book-outline" /> 掉落
                    </Button>
                    <div slot="content" class="drop-preview-content">
                      <template v-if="getGroupedDropItems(map).length">
                        <div class="drop-preview-title">预计掉落</div>
                        <div class="drop-preview-items">
                          <span
                            v-for="(drop, dropIndex) in getGroupedDropItems(map)"
                            :key="dropIndex"
                            class="drop-preview-item"
                          >
                            <span class="drop-preview-name">{{ getDropDisplayName(drop.itemName) }}</span><span class="drop-preview-group-text">【<span v-if="drop.levels && drop.levels.length">Lv.{{ drop.levels.join('、') }} </span><template v-for="(quality, qualityIndex) in drop.qualities"><span :key="qualityIndex" class="drop-preview-quality" :style="{ color: getDropColor(quality.color) }">{{ getDropQualityText(quality.color) || '普通' }}</span><span v-if="qualityIndex < drop.qualities.length - 1" :key="'separator-' + qualityIndex">、</span></template>】</span>
                          </span>
                        </div>
                      </template>
                      <div v-else class="drop-preview-empty">暂无掉落数据</div>
                    </div>
                  </Poptip>
                  <Button size="small" type="text" @click="viewMapMonsters(map)" title="查看怪物">
                    <Icon type="ios-skull-outline" /> 怪物
                  </Button>
                </span>
                <Icon v-if="selectedMap && selectedMap.mapId === map.mapId" type="ios-checkmark-circle" class="stage-map-item-check" />
                <Icon v-else type="ios-arrow-forward" class="stage-map-item-arrow" />
              </div>
            </div>
            <div v-else class="stage-map-empty">当前阶段暂无可进入的地图</div>
          </div>
          <div v-if="!stageGroups.some(stage => stage.maps.length)" class="stage-map-empty stage-map-empty--all">
            当前筛选下暂无地图
          </div>
        </div>

        <!-- 小地图层级 -->
        <div v-else class="sub-map-container">
          <!-- 当前区域信息 -->
          <div class="current-area-info">
            <Alert show-icon :type="getAreaType(currentBigMap.type)">
              <template slot="desc">
                {{ currentBigMap.description }}
                <span style="float: right;">
                  推荐等级：Lv.{{ currentBigMap.minLevel }} - Lv.{{ currentBigMap.maxLevel }}
                </span>
              </template>
            </Alert>
          </div>

          <!-- 小地图列表 -->
          <div class="sub-map-grid">
            <div
              v-for="subMap in filteredSubMaps"
              :key="subMap.mapId"
              class="sub-map-card"
              :class="{
                'selected': selectedMap && selectedMap.mapId === subMap.mapId,
                'recommended': isRecommended(subMap)
              }"
              @click="selectSubMap(subMap)"
            >
              <div class="sub-map-header">
                <img v-if="subMap.mapType==1" src="@/assets/stack.png" class="map-icon" />
                <img v-if="subMap.mapType==2" src="@/assets/grass.png" class="map-icon" />
                <img v-if="subMap.mapType==3" src="@/assets/tiaoz.png" class="map-icon" />
                <img v-if="subMap.mapType==4" src="@/assets/coin.png" class="map-icon" />
                <img v-if="subMap.mapType==5" src="@/assets/huodong.png" class="map-icon" />
                <img v-if="subMap.mapType==6" src="@/assets/boss.png" class="map-icon" />
                <img v-if="subMap.mapType==8" src="@/assets/putong.png" class="map-icon" />
                <span class="sub-map-name">{{ subMap.mapName }}</span>
              </div>

              <div class="sub-map-details">
                <div class="detail-row">
                  <span class="label">推荐等级</span>
                  <span class="value highlight">Lv.{{ subMap.minLv }} - Lv.{{ subMap.maxLv }}</span>
                </div>
                <div class="detail-row">
                  <span class="label">在线修士</span>
                  <span class="value">
                    <Icon type="ios-people" size="12" /> {{ subMap.onlineNum || 0 }}人
                  </span>
                </div>
                <div class="detail-row">
                  <span class="label">掉落情报</span>
                  <span class="value drops">
                    <Poptip trigger="click" word-wrap width="360" placement="top" class-name="drop-preview-poptip" transfer>
                      <Button size="small" type="text" class="drop-detail-btn">
                        <Icon type="ios-book-outline" /> 查看掉落
                      </Button>
                      <div slot="content" class="drop-preview-content">
                        <template v-if="getMapDrops(subMap) && getMapDrops(subMap).itemsVoList && getMapDrops(subMap).itemsVoList.length > 0">
                          <div class="drop-preview-title">掉落物品</div>
                          <div class="drop-preview-items">
                            <span
                              v-for="(item, idx) in getSortedDropItems(getMapDrops(subMap).itemsVoList)"
                              v-if="item"
                              :key="idx"
                              class="drop-preview-item"
                              :style="{ color: getDropColor(item.color) }"
                            >{{ getDropDisplayName(item.itemName) }}({{ getDropQualityText(item.color) }}/Lv.{{ item.level || 0 }})</span>
                          </div>
                        </template>
                        <div v-else class="drop-preview-empty">暂无掉落数据</div>
                      </div>
                    </Poptip>
                    <Button size="small" type="text" class="drop-detail-btn" @click.stop="viewMapMonsters(subMap)">
                      <Icon type="ios-skull-outline" /> 查看怪物
                    </Button>
                  </span>
                </div>
                <!-- 新增描述行 -->
                <div class="detail-row description-row">
                  <span class="label">描述</span>
                  <Poptip
                    trigger="hover"
                    word-wrap
                    width="220"
                    placement="top"
                    :content="subMap.describeTion"
                    :disabled="!subMap.describeTion || subMap.describeTion.length < 15"
                    class="description-poptip"
                  >
                    <span class="value description-text" :title="subMap.describeTion">
                      {{ subMap.describeTion || '暂无描述' }}
                    </span>
                  </Poptip>
                </div>
              </div>

              <!-- 选中标记 -->
              <div v-if="selectedMap && selectedMap.mapId === subMap.mapId" class="selected-mark">
                <Icon type="ios-checkmark-circle" color="#19be6b" size="24" />
              </div>

              <!-- 推荐标签 -->
              <div v-if="isRecommended(subMap)" class="recommend-tag">
                <Icon type="ios-star" /> 推荐
              </div>
            </div>
          </div>

          <!-- 底部操作栏 -->
          <div class="action-bar">
            <div class="selected-info" v-if="selectedMap">
              <span class="info-label">已选择：</span>
              <Tag color="primary" size="large">{{ selectedMap.mapName }}</Tag>
              <span class="level-hint">
                (推荐等级: Lv.{{ selectedMap.minLv }}-{{ selectedMap.maxLv }})
              </span>
            </div>
            <div class="action-buttons">
              <Button @click="refreshSubMaps" icon="ios-refresh" type="default">
                刷新
              </Button>
              <Button
                @click="startBattle"
                type="primary"
                icon="ios-play"
                :disabled="!selectedMap"
                :loading="battleLoading"
                size="large"
                class="battle-btn"
              >
                {{ battleLoading ? '进入战斗中...' : '开始战斗' }}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
export default {
  name: "MapSelector",
  // 添加 props 定义
  props: {
    // 地图列表数据
    mapList: {
      type: Array,
      default: () => []
    },
    // 用户活动数据
    userActivity: {
      type: Object,
      default: () => ({ level: 1 })
    },
    // 地图掉落数据
    mapLootsList: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      mapSelectorVisible: false,
      currentBigMap: null,
      selectedMap: null,
      selectedLevelRange: 'all',
      battleLoading: false,
      dungeonLoading: false,
      dungeonMaps: [],

      // 大地图数据（可按需扩展）
      bigMaps: [
        {
          id: 1,
          name: '落星山谷',
          description: '一望无际的芦苇荡，栖息着大量水鸟妖兽。迷雾中容易迷失方向。',
          minLevel: 1,
          maxLevel: 10,
          type: 1,
          typeText: '新手区域',
          icon: 'ios-leaf',
          color: '#52c41a',
          bgGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          subMaps: [] // 将从mapList填充
        },
        {
          id: 2,
          name: '大疆域-和州城',
          description: '和洲北处山脉连绵百里，在山脉西北有一座山，山如地中之肺，四周之洞为肺叶，地穴之水终日沸腾不止若肺管，故比之以肺。山脚密林内 有几道身影突然出现，正是刚刚从秘境传送出来的几位修士',
          minLevel: 12,
          maxLevel: 20,
          type: 2,
          typeText: '进阶区域',
          icon: 'ios-leaf',
          color: '#1890ff',
          bgGradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          subMaps: []
        },
        {
          id: 3,
          name: '黑潮群岛',
          description: '一片被黑色潮流环绕的险恶群岛，礁石如犬牙交错。此处是海盗们的巢穴之一，他们与驯服的海兽一同，劫掠过往船只。',
          minLevel: 20,
          maxLevel: 30,
          type: 3,
          typeText: '高级区域',
          icon: 'ios-cloud',
          color: '#722ed1',
          bgGradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          subMaps: []
        },
        {
          id: 4,
          name: '方丈仙山',
          description: '海外三仙山之一方丈山的遗迹，高僧讲经的石台仍残留着无上佛法道韵。在此悟道，可淬炼元婴，感悟天地法则。',
          minLevel: 30,
          maxLevel: 40,
          type: 4,
          typeText: '挑战区域',
          icon: 'ios-flame',
          color: '#fa541c',
          bgGradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          subMaps: []
        },
        {
          id: 5,
          name: '禁地',
          description: '极度危险的禁忌之地，只有大能者才敢踏足',
          minLevel: 45,
          maxLevel: 50,
          type: 5,
          typeText: '极限区域',
          icon: 'ios-warning',
          color: '#f5222d',
          bgGradient: 'linear-gradient(135deg, #434343 0%, #000000 100%)',
          subMaps: []
        }
      ]
    };
  },

  computed: {
    // 根据等级筛选后的大地图
    filteredBigMaps() {
      if (this.selectedLevelRange === 'all') {
        return this.bigMaps;
      }

      const [min, max] = this.selectedLevelRange.split('-').map(Number);
      return this.bigMaps.filter(map => {
        // 显示与等级区间有交集的大地图
        return (map.minLevel <= max && map.maxLevel >= min) ||
          map.minLevel <= min;
      });
    },

    // 普通地图按最低等级归入六个阶段，完整展示符合条件的地图
    stageGroups() {
      const stages = [
        { key: 'stage-1', label: '凡境启灵', range: '1 - 12级', max: 12, hint: '汀上来客→雪鸮引路→槐灵秘闻→镜湖问脉', icon: 'ios-leaf', theme: 'wood', accent: '#3f8a68' },
        { key: 'stage-2', label: '和洲疑云', range: '13 - 20级', max: 20, hint: '和洲大会→照骨问道→洄潮寻驿→地动山摇', icon: 'ios-water', theme: 'water', accent: '#287fa9' },
        { key: 'stage-3', label: '海外诸岛', range: '21 - 30级', max: 30, hint: '海泽初航→暗流哨探→遗宝沉船→蜃楼疑踪', icon: 'ios-moon', theme: 'moon', accent: '#7160ad' },
        { key: 'stage-4', label: '归墟余波', range: '31 - 40级', max: 40, hint: '海潮拾遗→龙骨修舟→无灯寻档→蜃潮辨路', icon: 'ios-cloud', theme: 'cloud', accent: '#c05a5a' },
        { key: 'stage-5', label: '残云遗址', range: '41 - 50级', max: 50, hint: '废圃复阵→九阙识云→归舟取证→镇云入关', icon: 'ios-flame', theme: 'fire', accent: '#a94b55' },
        { key: 'stage-6', label: '古脉续生', range: '51级以上', max: Infinity, hint: '碎图入巷→沉脉开门→三髓入府→封台分髓', icon: 'ios-home', theme: 'spirit', accent: '#c9783f' }
      ];
      const groups = stages.map(stage => ({ ...stage, maps: [] }));
      const seen = new Set();
      (this.mapList || []).forEach(map => {
        if (!map || this.isSpecialMap(map) || seen.has(String(map.mapId))) return;
        const minLv = Number(map.minLv) || 1;
        const index = stages.findIndex(stage => minLv <= stage.max);
        if (index >= 0) {
          groups[index].maps.push({
            ...map,
            isDungeonMap: false,
            stageTheme: stages[index].theme,
            stageAccent: stages[index].accent,
          });
          seen.add(String(map.mapId));
        }
      });
      groups.forEach(group => group.maps.sort((a, b) => (Number(a.minLv) || 0) - (Number(b.minLv) || 0)));
      return groups;
    },

    // 当前大地图下的小地图（应用等级筛选）
    filteredSubMaps() {
      if (!this.currentBigMap) return [];

      let maps = this.currentBigMap.subMaps;

      if (this.selectedLevelRange !== 'all' && this.selectedLevelRange !== 'dungeon') {
        const [min, max] = this.selectedLevelRange.split('-').map(Number);
        maps = maps.filter(m => m.minLv <= max && m.maxLv >= min);
      }

      return maps.sort((a, b) => a.minLv - b.minLv);
    },
    dungeonSubMaps() {
      return (this.dungeonMaps || []).slice().sort((a, b) => {
        const leftMin = Number(a && a.minLv) || 0;
        const rightMin = Number(b && b.minLv) || 0;
        return leftMin - rightMin;
      });
    },
    specialFilteredMaps() {
      if (this.selectedLevelRange === 'dungeon') return this.dungeonSubMaps;
      return (this.mapList || []).filter(map => this.isActivityMap(map)).sort((a, b) => (Number(a.minLv) || 0) - (Number(b.minLv) || 0));
    },

  },

  methods: {

    isActivityMap(map) {
      const name = String(map && (map.mapName || map.name) || '');
      const typeText = String(map && (map.typeText || map.mapTypeName || '') || '');
      return Number(map && map.mapType) === 5 || name.includes('活动') || typeText.includes('活动');
    },
    isSpecialMap(map) {
      return !!(map && map.isDungeonMap) || this.isActivityMap(map);
    },
    getMapIcon(map) {
      if (map && map.isDungeonMap) return 'ios-flask';
      if (this.isActivityMap(map)) return 'ios-calendar';
      const name = String(map && (map.mapName || map.name) || '');
      if (/水|湖|河|海|渡|潮|溪|湾|岛/.test(name)) return 'ios-water';
      if (/山|谷|林|森|岭|原|野/.test(name)) return 'ios-leaf';
      if (/洞|府|宫|城|关|门|殿/.test(name)) return 'ios-home';
      if (/遗|墟|墓|塔|台|阵/.test(name)) return 'ios-planet';
      if (/火|炎|焰|赤/.test(name)) return 'ios-flame';
      if (/云|天|星|月|霄/.test(name)) return 'ios-cloud';
      const minLv = Number(map && map.minLv) || 1;
      if (minLv <= 10) return 'ios-leaf';
      if (minLv <= 20) return 'ios-water';
      if (minLv <= 30) return 'ios-moon';
      if (minLv <= 40) return 'ios-cloud';
      if (minLv <= 50) return 'ios-flame';
      return 'ios-home';
    },
    getMapAccentColor(map) {
      if (map && map.stageAccent) return map.stageAccent;
      if (map && map.isDungeonMap) return '#7a6ab3';
      if (this.isActivityMap(map)) return '#b27655';
      const name = String(map && (map.mapName || map.name) || '');
      if (/水|湖|河|海|渡|潮|溪|湾|岛/.test(name)) return '#367fa4';
      if (/山|谷|林|森|岭|原|野/.test(name)) return '#4d866e';
      if (/洞|府|宫|城|关|门|殿/.test(name)) return '#566f9f';
      if (/遗|墟|墓|塔|台|阵/.test(name)) return '#6b6da5';
      if (/火|炎|焰|赤/.test(name)) return '#a8615e';
      if (/云|天|星|月|霄/.test(name)) return '#527aa6';
      const minLv = Number(map && map.minLv) || 1;
      if (minLv <= 12) return '#4d866e';
      if (minLv <= 20) return '#367fa4';
      if (minLv <= 30) return '#6b6da5';
      if (minLv <= 40) return '#527aa6';
      if (minLv <= 50) return '#a8615e';
      return '#566f9f';
    },

    // 打开地图选择器
    openMapSelector() {
      this.mapSelectorVisible = true;
      this.currentBigMap = null;
      this.selectedMap = null;
      this.selectedLevelRange = 'all';
      this.distributeMapsToBigMaps();
    },

    // 关闭地图选择器
    closeMapSelector() {
      this.mapSelectorVisible = false;
    },

    // 将后端地图数据分配到各个大地图
    distributeMapsToBigMaps() {
      // 清空现有数据
      this.bigMaps.forEach(bm => bm.subMaps = []);

      // 根据等级范围将地图分配到对应的大地图
      this.mapList.forEach(map => {
        // 找到最适合的大地图
        const targetBigMap = this.bigMaps.find(bm => {
          return map.minLv >= bm.minLevel && map.maxLv <= bm.maxLevel;
        }) || this.bigMaps[0]; // 默认分配到人间境

        if (targetBigMap) {
          targetBigMap.subMaps.push({
            ...map,
          });
        }
      });
    },

    // 解析地图掉落（简化显示）
    parseDrops(map) {
      // 这里可以根据实际需求从mapDropItems或其他数据源获取
      const dropSource = (this.mapLootsList || []).find((item) => {
        if (!item) return false;
        if (item.mapId != null && map.mapId != null) {
          return String(item.mapId) === String(map.mapId);
        }
        return item.mapName === map.mapName;
      });

      const items = dropSource && Array.isArray(dropSource.itemsVoList)
        ? dropSource.itemsVoList
        : [];

      return [...new Set(
        items
          .map((item) => item && item.itemName)
          .filter(Boolean)
      )].slice(0, 3);
    },

    // 获取大地图样式
    getBigMapStyle(bigMap) {
      return {
        background: bigMap.bgGradient,
      };
    },

    // 获取地图类型颜色
    getMapTypeColor(type) {
      const colors = {
        1: 'green',
        2: 'blue',
        3: 'purple',
        4: 'orange',
        5: 'red'
      };
      return colors[type] || 'default';
    },

    getAreaType(type) {
      const types = {
        1: 'success',
        2: 'info',
        3: 'warning',
        4: 'error',
        5: 'error'
      };
      return types[type] || 'info';
    },

    // 进入大地图
    enterBigMap(bigMap) {
      if (this.userActivity.level < bigMap.minLevel) {
        this.$Message.warning(`需要达到${bigMap.minLevel}级才能进入${bigMap.name}`);
        return;
      }

      this.currentBigMap = bigMap;
      this.selectedMap = null;

      // 自动推荐最适合当前等级的地图
      const recommended = bigMap.subMaps.find(m =>
        this.userActivity.level >= m.minLv &&
        this.userActivity.level <= m.maxLv
      );
      if (recommended) {
        this.selectedMap = recommended;
      }
    },

    // 返回大地图
    backToBigMap() {
      this.currentBigMap = null;
      this.selectedMap = null;
    },

    // 选择小地图
    selectSubMap(subMap) {
      this.selectedMap = subMap;
    },
    viewMapDrops(subMap) {
      this.mapSelectorVisible = false;
      this.$emit('view-drops', subMap);
    },
    viewMapMonsters(subMap) {
      this.mapSelectorVisible = false;
      this.$emit('view-monsters', subMap);
    },

    // 判断是否推荐（适合当前等级）
    isRecommended(subMap) {
      const level = this.userActivity.level;
      return level >= subMap.minLv && level <= subMap.maxLv;
    },
    // 根据地图名获取掉落数据
    getMapDrops(subMap) {
      if (!subMap) return null;
      const mapId = subMap.mapId == null ? '' : String(subMap.mapId);
      const mapName = String(subMap.mapName || '').replace(/\s+/g, '').trim();
      const source = Array.isArray(this.mapLootsList) ? this.mapLootsList : [];
      return source.find((item) => {
        if (!item) return false;
        if (mapId && item.mapId != null && String(item.mapId) === mapId) return true;
        const itemName = String(item.mapName || item.name || '').replace(/\s+/g, '').trim();
        return !!mapName && itemName === mapName;
      }) || null;
    },
    getGroupedDropItems(subMap) {
      const source = this.getMapDrops(subMap);
      const items = source && Array.isArray(source.itemsVoList) ? source.itemsVoList.filter(Boolean) : [];
      const grouped = {};
      items.forEach((item) => {
        const name = String(item.itemName || item.name || '').trim();
        if (!name) return;
        if (!grouped[name]) grouped[name] = { itemName: name, qualities: [], levels: [] };
        const color = Number(item.color) || 0;
        if (!grouped[name].qualities.some((quality) => Number(quality.color) === color)) {
          grouped[name].qualities.push({ color });
        }
        const level = Number(item.level);
        if (Number.isFinite(level) && !grouped[name].levels.includes(level)) grouped[name].levels.push(level);
      });
      return Object.keys(grouped).map((name) => {
        grouped[name].qualities.sort((left, right) => right.color - left.color);
        grouped[name].levels.sort((left, right) => left - right);
        return grouped[name];
      });
    },
    // 获取掉落物品的颜色
    getDropColor(color) {
      const colorMap = {
        1: '#8c94a4',
        2: '#879f1e',
        3: '#078599',
        4: '#036cbe',
        5: '#801DAE',
        6: '#9e0e42',
        7: '#FFA631',
      };
      return colorMap[color] || '#657180';
    },
    // 获取掉落物品的品质文字
    getDropQualityText(color) {
      const qualityMap = {
        1: '破损',
        2: '普通',
        3: '良品',
        4: '上品',
        5: '绝品',
        6: '灵宝',
        7: '秘宝',
      };
      return qualityMap[color] || '';
    },
    getDropDisplayName(name) {
      return String(name || '').replace(/^\s*[\[【](破损|普通|良品|上品|绝品|灵宝|秘宝)[\]】]\s*/, '').trim();
    },
    // 按品质排序掉落物品（从高到低，7=秘宝最高）
    getSortedDropItems(items) {
      return (items || []).slice().sort((a, b) => {
        const colorA = Number(a && a.color) || 0;
        const colorB = Number(b && b.color) || 0;
        return colorB - colorA;
      });
    },

    async loadDungeonMaps(forceRefresh = false) {
      if (!forceRefresh && this.dungeonMaps.length > 0) {
        return;
      }

      this.dungeonLoading = true;
      try {
        const res = await this.$http.get('/gameAmry/queryMapList');
        this.dungeonMaps = ((res.data && res.data.data) || []).map(map => ({
          ...map,
          isDungeonMap: true,
        }));
        if (this.selectedMap && this.selectedMap.isDungeonMap) {
          const current = this.dungeonMaps.find(map => String(map.mapId) === String(this.selectedMap.mapId));
          this.selectedMap = current || null;
        }
      } catch (error) {
        this.$Message.error('副本地图加载失败');
        this.dungeonMaps = [];
      } finally {
        this.dungeonLoading = false;
      }
    },

    // 等级筛选变化
    filterByLevel(range) {
      if (range === 'dungeon') {
        this.currentBigMap = null;
        this.selectedMap = null;
        this.loadDungeonMaps();
        return;
      }

      if (range === 'activity') {
        this.currentBigMap = null;
        this.selectedMap = null;
        return;
      }

      if (this.selectedMap && this.selectedMap.isDungeonMap) {
        this.selectedMap = null;
      }
    },

    // 刷新小地图数据
    refreshSubMaps() {
      if (this.selectedLevelRange === 'dungeon') {
        this.loadDungeonMaps(true);
        this.$Message.success('副本地图已刷新');
        return;
      }

      if (this.selectedLevelRange === 'activity') {
        this.$parent.MapList();
        setTimeout(() => this.$forceUpdate(), 500);
        this.$Message.success('活动地图已刷新');
        return;
      }

      this.$parent.MapList();
      setTimeout(() => this.distributeMapsToBigMaps(), 500);
      this.$Message.success('地图数据已刷新');
    },

    // 修改开始战斗方法，使用 $emit 通知父组件
    async startBattle() {
      if (!this.selectedMap) {
        this.$Message.warning('请先选择一个地图');
        return;
      }

      this.battleLoading = true;

      try {
        // 关闭选择器
        this.mapSelectorVisible = false;

        // 通过事件通知父组件开始战斗
        this.$emit('start-battle', this.selectedMap);

      } catch (error) {
        this.$Message.error('操作失败');
        console.error(error);
      } finally {
        this.battleLoading = false;
      }
    },
  }
};
</script>

<style scoped>
/* ========== 模态框整体样式 ========== */
.map-selector-modal >>> .ivu-modal-body {
  padding: 0;
  background: var(--bg-primary, #f5f7f9);
}

.map-selector-container {
  display: flex;
  flex-direction: column;
  height: 650px;
  max-height: 80vh;
}

/* ========== 头部样式 ========== */
.selector-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #fff;
  border-bottom: 1px solid #e8eaec;
}

.selector-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-actions {
  display: flex;
  gap: 8px;
}

/* ========== 等级筛选 ========== */
.level-filter {
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e8eaec;
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-label {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

/* ========== 大地图网格 ========== */
.big-map-grid {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
  background: #f5f7f9;
}

.big-map-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  position: relative;
}

.big-map-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
}

.big-map-card.locked {
  opacity: 0.6;
  cursor: not-allowed;
}

.big-map-card.locked:hover {
  transform: none;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

/* 地图视觉区域 */
.map-visual {
  height: 140px;
  position: relative;
}

.map-bg {
  width: 100%;
  height: 100%;
  position: relative;
}

.map-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.2);
  color: white;
}

.level-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0,0,0,0.6);
  color: #fff;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

/* 地图信息 */
.map-info {
  padding: 16px;
}

.map-info h3 {
  margin: 0 0 8px 0;
  font-size: 16px;
  color: #333;
}

.map-desc {
  margin: 0 0 12px 0;
  font-size: 12px;
  color: #666;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.map-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.map-count {
  font-size: 12px;
  color: #999;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 锁定遮罩 */
.lock-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  backdrop-filter: blur(2px);
}

.lock-overlay p {
  margin-top: 8px;
  font-size: 12px;
}

/* ========== 小地图层级 ========== */
.sub-map-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f5f7f9;
}

.current-area-info {
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e8eaec;
}

/* 小地图网格 */
.dungeon-grid-wrap {
  flex: 1;
  position: relative;
}

.sub-map-grid {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.sub-map-card {
  background: #fff;
  border-radius: 8px;
  padding: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  position: relative;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}

.sub-map-card:hover {
  border-color: #2d8cf0;
  box-shadow: 0 4px 12px rgba(45,140,240,0.15);
}

.sub-map-card.selected {
  border-color: #19be6b;
  background: #f6ffed;
}

.sub-map-card.recommended {
  border-color: #faad14;
}

.sub-map-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.map-icon {
  width: 20px;
  height: 20px;
  opacity: 0.8;
}

.sub-map-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.sub-map-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
}

.detail-row .label {
  color: #999;
}

.detail-row .value {
  color: #666;
  font-weight: 500;
}

.detail-row .value.highlight {
  color: #2d8cf0;
}

.detail-row .value.drops {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.drop-detail-btn {
  padding: 0 !important;
  height: auto !important;
}

.empty-drop-text {
  font-size: 12px;
  color: #999;
}

/* 选中标记 */
.selected-mark {
  position: absolute;
  top: 8px;
  right: 8px;
}

/* 推荐标签 */
.recommend-tag {
  position: absolute;
  top: -2px;
  left: 12px;
  background: #faad14;
  color: #fff;
  padding: 2px 8px;
  border-radius: 0 0 4px 4px;
  font-size: 10px;
  display: flex;
  align-items: center;
  gap: 2px;
  box-shadow: 0 2px 4px rgba(250,173,20,0.3);
}

/* ========== 底部操作栏 ========== */
.action-bar {
  padding: 16px 20px;
  background: #fff;
  border-top: 1px solid #e8eaec;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
/* 描述行特殊样式 */
.description-row {
  align-items: flex-start !important; /* 顶部对齐，多行时好看 */
}

/* 核心：两行省略号 */
.description-text {
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;        /* 限制两行 */
  -webkit-box-orient: vertical !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  line-height: 1.5 !important;
  max-height: 3em !important;            /* 2行 * 1.5行高 */
  word-break: break-word !important;       /* 长单词换行 */
  flex: 1 !important;
  text-align: left !important;
}

/* Poptip触发区域撑满 */
.description-poptip {
  flex: 1;
  display: flex;
}

.description-poptip >>> .ivu-poptip-rel {
  flex: 1;
  display: flex;
}

/* 深色主题适配 */
.dark-theme .description-text {
  color: #9c9cab;
}
.selected-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-label {
  font-size: 13px;
  color: #666;
}

.level-hint {
  font-size: 12px;
  color: #999;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.battle-btn {
  min-width: 120px;
  font-weight: 500;
}

/* ========== 响应式适配 ========== */
@media (max-width: 768px) {
  .map-selector-container {
    height: var(--app-height);
    max-height: var(--app-height);
  }

  .big-map-grid {
    grid-template-columns: 1fr;
    padding: 12px;
  }

  .sub-map-grid {
    grid-template-columns: 1fr;
    padding: 12px;
  }

  .action-bar {
    flex-direction: column;
    gap: 12px;
  }

  .action-buttons {
    width: 100%;
  }

  .battle-btn {
    flex: 1;
  }
}

/* ========== 深色主题适配 ========== */
.dark-theme .map-selector-modal >>> .ivu-modal-body {
  background: #151728;
}

.dark-theme .selector-header,
.dark-theme .level-filter,
.dark-theme .current-area-info,
.dark-theme .action-bar {
  background: #1e1e2e;
  border-color: rgba(73,117,212,0.2);
  color: #9c9cab;
}

.dark-theme .big-map-grid,
.dark-theme .sub-map-container {
  background: #151728;
}

.dark-theme .big-map-card,
.dark-theme .sub-map-card {
  background: #1e1e2e;
  border-color: rgba(73,117,212,0.2);
}

.dark-theme .map-info h3,
.dark-theme .sub-map-name {
  color: #fff;
}

.dark-theme .map-desc,
.dark-theme .detail-row .label {
  color: #808695;
}

.dark-theme .map-count,
.dark-theme .detail-row .value {
  color: #9c9cab;
}

.dark-theme .sub-map-card.selected {
  background: rgba(25,190,107,0.1);
  border-color: #19be6b;
}

/* 掉落预览悬浮窗 */
.drop-preview-content {
  max-height: 320px;
  overflow-y: auto;
}
.drop-preview-title {
  font-size: 12px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
  padding-bottom: 4px;
  border-bottom: 1px solid #eee;
}
.drop-preview-items {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
}
.drop-preview-item {
  font-size: 12px;
  padding: 2px 0;
  white-space: nowrap;
}
.drop-preview-name { color: #315a7c; font-weight: 700; }
.drop-preview-quality { font-weight: 700; }
.drop-preview-group-text { margin-left: 4px; color: #6f8da6; font-weight: 600; }
/* 掉落预览悬浮窗 - 确保浮在地图弹窗之上 */
:global(.drop-preview-poptip.ivu-poptip-popper) {
  z-index: 10010 !important;
}

.drop-preview-empty {
  font-size: 12px;
  color: #999;
  text-align: center;
  padding: 8px 0;
}
</style>

<style>
/* Map selector: compact six-stage layout and clear mode filters. */
.map-selector-modal .level-filter {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: #ffffff;
  border-bottom: 1px solid #c9dced;
}
.map-selector-modal .level-filter .filter-label {
  color: #426581;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}
.map-selector-modal .level-filter .ivu-radio-group-button {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}
.map-selector-modal .level-filter .ivu-radio-group-button .ivu-radio-wrapper {
  height: 28px;
  padding: 4px 11px;
  color: #426581;
  background: #f7fbff;
  border: 1px solid #b9cde0;
  border-radius: 4px;
  line-height: 18px;
  font-size: 11px;
}
.map-selector-modal .level-filter .ivu-radio-group-button .ivu-radio-wrapper:before,
.map-selector-modal .level-filter .ivu-radio-group-button .ivu-radio-wrapper:after { display: none; }
.map-selector-modal .level-filter .ivu-radio-group-button .ivu-radio-wrapper-checked {
  color: #ffffff;
  background: #3978b7;
  border-color: #2f669d;
  box-shadow: none;
}
.map-selector-modal .map-filter-battle-button {
  flex: 0 0 auto;
  height: 30px;
  margin-left: auto;
  padding: 4px 11px;
  border-radius: 4px;
  white-space: nowrap;
}
.map-selector-modal .map-filter-battle-button.ivu-btn-primary {
  color: #ffffff;
  background: #3978b7;
  border-color: #2f669d;
}
.map-selector-modal .big-map-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  padding: 10px 14px;
  background: #f5f9fc;
}
.map-selector-modal .stage-map-section {
  min-width: 0;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #c9dced;
  border-radius: 6px;
}
.map-selector-modal .stage-map-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 7px;
  padding: 9px 10px;
  background: #f7fbff;
  border-bottom: 1px solid #dbe8f2;
}
.map-selector-modal .stage-map-title {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  color: #315a7c;
}
.map-selector-modal .stage-map-title .ivu-icon { color: #3978b7; font-size: 16px; }
.map-selector-modal .stage-map-title strong { font-size: 13px; white-space: nowrap; }
.map-selector-modal .stage-map-range {
  padding: 2px 5px;
  color: #315a7c;
  background: #eef6fd;
  border: 1px solid #c9dced;
  border-radius: 3px;
  font-weight: 700;
}
.map-selector-modal .stage-map-title span,
.map-selector-modal .stage-map-header > small { color: #7895aa; font-size: 9px; white-space: nowrap; }
.map-selector-modal .stage-map-header > small { overflow: hidden; text-overflow: ellipsis; }
.map-selector-modal .stage-map-list {
  height: auto;
  padding: 4px;
  overflow: visible;
}
.map-selector-modal .stage-map-item {
  display: flex;
  align-items: center;
  gap: 7px;
  width: 100%;
  min-height: 46px;
  margin: 0;
  padding: 6px 7px;
  color: #426581;
  background: #ffffff;
  border: 0;
  border-bottom: 1px solid #edf3f7;
  cursor: pointer;
  text-align: left;
}
.map-selector-modal .stage-map-item:last-child { border-bottom: 0; }
.map-selector-modal .stage-map-item:hover { background: #f7fbff; }
.map-selector-modal .stage-map-item.selected { background: #eef6fd; box-shadow: inset 3px 0 #3978b7; }
.map-selector-modal .stage-map-item.selected .stage-map-item-main strong,
.map-selector-modal .stage-map-item.selected .stage-map-item-icon { color: #3978b7 !important; }
.map-selector-modal .stage-map-item-icon { flex: 0 0 auto; color: #5f96bf; font-size: 17px; }
.map-selector-modal .stage-map-item-main { display: grid; gap: 2px; min-width: 0; flex: 1 1 auto; }
.map-selector-modal .stage-map-item-main strong,
.map-selector-modal .stage-map-item-main small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.map-selector-modal .stage-map-item-main strong { color: #315a7c; font-size: 14px; font-weight: 700; }
.map-selector-modal .stage-map-item-main small { color: #7895aa; font-size: 12px; }
.map-selector-modal .stage-map-item-description {
  display: block;
  min-width: 0;
  overflow: hidden;
  color: #8aa4b8;
  font-size: 12px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.map-selector-modal .stage-map-item-description .ivu-poptip-rel {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.map-selector-modal .stage-map-item-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 2px;
}
.map-selector-modal .stage-map-item-actions .ivu-btn {
  height: 24px;
  padding: 2px 4px;
  color: #5f8eb6;
  font-size: 12px;
}
.map-selector-modal .stage-map-item-actions .ivu-btn:hover {
  color: #315a7c;
  background: #eef6fd;
}
.map-selector-modal .stage-map-section {
  border-top: 3px solid #7fa9ca;
}
.map-selector-modal .stage-map-section--wood { border-top-color: #5f9b86; }
.map-selector-modal .stage-map-section--water { border-top-color: #4d9bc2; }
.map-selector-modal .stage-map-section--moon { border-top-color: #7776b7; }
.map-selector-modal .stage-map-section--cloud { border-top-color: #c05a5a; }
.map-selector-modal .stage-map-section--fire { border-top-color: #a94b55; }
.map-selector-modal .stage-map-section--spirit { border-top-color: #c9783f; }
.map-selector-modal .stage-map-section--wood .stage-map-title .ivu-icon { color: #5f9b86; }
.map-selector-modal .stage-map-section--water .stage-map-title .ivu-icon { color: #4d9bc2; }
.map-selector-modal .stage-map-section--moon .stage-map-title .ivu-icon { color: #7776b7; }
.map-selector-modal .stage-map-section--cloud .stage-map-title .ivu-icon { color: #c05a5a; }
.map-selector-modal .stage-map-section--fire .stage-map-title .ivu-icon { color: #a94b55; }
.map-selector-modal .stage-map-section--spirit .stage-map-title .ivu-icon { color: #c9783f; }
.map-selector-modal .stage-map-item-check { flex: 0 0 auto; color: #3978b7; font-size: 17px; }
.map-selector-modal .stage-map-item-arrow { flex: 0 0 auto; color: #a7bdcc; font-size: 13px; }
.map-selector-modal .stage-map-empty { padding: 18px 10px; color: #8aa4b8; text-align: center; font-size: 11px; }
.map-selector-modal .stage-map-empty--all { grid-column: 1 / -1; }
.map-selector-modal .special-map-container .sub-map-card { background: #ffffff; border-color: #c9dced; box-shadow: none; }
.map-selector-modal .special-map-container .sub-map-card.selected { background: #eef6fd; border-color: #3978b7; }
.map-selector-modal .special-map-container .sub-map-card.recommended { border-color: #7fa9ca; }
@media (max-width: 720px) {
  .map-selector-modal .level-filter { align-items: flex-start; flex-direction: column; gap: 6px; padding: 8px 10px; }
  .map-selector-modal .level-filter .ivu-radio-group-button { width: 100%; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .map-selector-modal .level-filter .ivu-radio-group-button .ivu-radio-wrapper { padding: 4px 3px; text-align: center; }
  .map-selector-modal .map-filter-battle-button {
    width: 100%;
    margin-left: 0;
  }
  .map-selector-modal .big-map-grid { grid-template-columns: 1fr; gap: 7px; padding: 7px 9px; }
  .map-selector-modal .stage-map-header { padding: 8px; }
  .map-selector-modal .stage-map-header > small { max-width: 42%; }
  .map-selector-modal .stage-map-title { gap: 4px; }
  .map-selector-modal .stage-map-title strong { font-size: 12px; }
  .map-selector-modal .stage-map-title span { font-size: 10px; }
  .map-selector-modal .stage-map-list { height: auto; overflow: visible; }
  .map-selector-modal .stage-map-item {
    align-items: flex-start;
    min-height: 62px;
    padding: 7px 6px;
  }
  .map-selector-modal .stage-map-item-icon { margin-top: 2px; }
  .map-selector-modal .stage-map-item-main { padding-top: 1px; }
  .map-selector-modal .stage-map-item-actions {
    display: grid;
    gap: 1px;
  }
  .map-selector-modal .stage-map-item-actions .ivu-btn {
    height: 22px;
    padding: 1px 3px;
    font-size: 9px;
  }
  .map-selector-modal .stage-map-item-check,
  .map-selector-modal .stage-map-item-arrow { display: none; }
}
</style>

<style>
/* Let every stage expand naturally; scrolling belongs to the modal body. */
.map-selector-modal .map-selector-container {
  height: auto !important;
  max-height: none !important;
}
.map-selector-modal .big-map-grid {
  flex: none !important;
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
}
.map-selector-modal .stage-map-section {
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
}
.map-selector-modal .stage-map-list {
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
}
.map-selector-modal .ivu-modal-body {
  max-height: 82vh !important;
  overflow-x: hidden !important;
  overflow-y: auto !important;
}
@media (max-width: 720px) {
  .map-selector-modal .map-selector-container {
    min-height: 0 !important;
  }
  .map-selector-modal .ivu-modal-body {
    max-height: 100vh !important;
  }
}
</style>

<style>
/* Enlarge map-list text only; drop preview typography remains unchanged. */
.map-selector-modal .stage-map-title strong { font-size: 14px; }
.map-selector-modal .stage-map-title span,
.map-selector-modal .stage-map-header > small { font-size: 12px; }
.map-selector-modal .stage-map-item-main strong { font-size: 14px; }
.map-selector-modal .stage-map-item-main small,
.map-selector-modal .stage-map-item-description { font-size: 12px; }
.map-selector-modal .stage-map-item-actions .ivu-btn { font-size: 12px; }
.map-selector-modal .map-filter-battle-button {
  flex: 0 0 auto;
  height: 30px;
  margin-left: auto;
  padding: 4px 11px;
  color: #ffffff;
  background: #3978b7;
  border-color: #2f669d;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
}
.map-selector-modal .map-filter-battle-button:hover {
  background: #2f669d;
  border-color: #285a8d;
}
@media (max-width: 720px) {
  .map-selector-modal .map-filter-battle-button {
    width: 100%;
    margin-left: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/MapSelector.vue